import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

export interface MerchantCredentials {
  loginUrl: string;
  user: string;
  password: string;
  apiKey?: string;
  apiSecret?: string;
  merchantName?: string;
}

type FormatKey = 'emoji' | 'bilingual' | 'markdown';

interface CredentialsCopyBlockProps {
  credentials: MerchantCredentials;
  className?: string;
}

const padRight = (s: string, len: number) => {
  if (s.length >= len) return s;
  return s + ' '.repeat(len - s.length);
};

const buildEmoji = (c: MerchantCredentials): string => {
  const lines: string[] = [];
  lines.push(`🔐 ข้อมูลเข้าใช้งานระบบ ${process.env.REACT_APP_WEB_NAME || 'Go2Pay'}`);
  if (c.merchantName) lines.push(`👤 Merchant: ${c.merchantName}`);
  lines.push('');
  lines.push(`${padRight('URL Login', 9)} : ${c.loginUrl}`);
  lines.push(`${padRight('User', 9)} : ${c.user}`);
  lines.push(`${padRight('Password', 9)} : ${c.password}`);
  if (c.apiKey) {
    lines.push('');
    lines.push('📡 API Credentials');
    lines.push(`${padRight('API', 7)} : ${c.apiKey}`);
    if (c.apiSecret) lines.push(`${padRight('Secret', 7)} : ${c.apiSecret}`);
  }
  lines.push('');
  lines.push('⚠️ กรุณาเก็บข้อมูลนี้เป็นความลับ และเปลี่ยนรหัสผ่านหลังเข้าใช้ครั้งแรก');
  return lines.join('\n');
};

const buildBilingual = (c: MerchantCredentials): string => {
  const lines: string[] = [];
  lines.push('🔐 ข้อมูลเข้าใช้งานระบบ / Login Credentials');
  if (c.merchantName) lines.push(`👤 Merchant: ${c.merchantName}`);
  lines.push('');
  lines.push('— เข้าระบบ / Sign in —');
  lines.push(`URL Login (ลิงก์เข้าระบบ) : ${c.loginUrl}`);
  lines.push(`User (ชื่อผู้ใช้)        : ${c.user}`);
  lines.push(`Password (รหัสผ่าน)      : ${c.password}`);
  if (c.apiKey) {
    lines.push('');
    lines.push('— 📡 API Credentials (กุญแจเชื่อมต่อ API) —');
    lines.push(`API Key (กุญแจ API)   : ${c.apiKey}`);
    if (c.apiSecret) lines.push(`API Secret (รหัสลับ)  : ${c.apiSecret}`);
  }
  lines.push('');
  lines.push('⚠️ กรุณาเก็บข้อมูลเป็นความลับ และเปลี่ยนรหัสผ่านหลังเข้าใช้ครั้งแรก');
  lines.push('⚠️ Please keep this information confidential and change your password after the first login.');
  return lines.join('\n');
};

const buildMarkdown = (c: MerchantCredentials): string => {
  const lines: string[] = [];
  lines.push(`## 🔐 ${process.env.REACT_APP_WEB_NAME || 'Go2Pay'} Login Credentials`);
  if (c.merchantName) lines.push(`**Merchant:** ${c.merchantName}`);
  lines.push('');
  lines.push('| Field | Value |');
  lines.push('|-------|-------|');
  lines.push(`| **URL Login** | ${c.loginUrl} |`);
  lines.push(`| **User** | ${c.user} |`);
  lines.push(`| **Password** | \`${c.password}\` |`);
  if (c.apiKey) {
    lines.push('');
    lines.push('### 📡 API Credentials');
    lines.push('');
    lines.push('| Field | Value |');
    lines.push('|-------|-------|');
    lines.push(`| **API** | \`${c.apiKey}\` |`);
    if (c.apiSecret) lines.push(`| **Secret** | \`${c.apiSecret}\` |`);
  }
  lines.push('');
  lines.push('> ⚠️ กรุณาเก็บข้อมูลนี้เป็นความลับ และเปลี่ยนรหัสผ่านหลังเข้าใช้ครั้งแรก');
  return lines.join('\n');
};

const FORMATS: { key: FormatKey; label: string; sublabel: string }[] = [
  { key: 'emoji', label: 'Emoji', sublabel: 'แบบสวย' },
  { key: 'bilingual', label: 'TH/EN', sublabel: '2 ภาษา' },
  { key: 'markdown', label: 'Markdown', sublabel: 'Notion/Slack' },
];

const CredentialsCopyBlock: React.FC<CredentialsCopyBlockProps> = ({ credentials, className = '' }) => {
  const [format, setFormat] = useState<FormatKey>('emoji');
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    switch (format) {
      case 'bilingual': return buildBilingual(credentials);
      case 'markdown': return buildMarkdown(credentials);
      case 'emoji':
      default: return buildEmoji(credentials);
    }
  }, [format, credentials]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Format selector */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
        {FORMATS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFormat(f.key)}
            className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-semibold transition-all duration-200 ${
              format === f.key
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="leading-tight">{f.label}</div>
            <div className={`text-[9px] font-normal mt-0.5 ${format === f.key ? 'text-gray-400' : 'text-gray-400'}`}>
              {f.sublabel}
            </div>
          </button>
        ))}
      </div>

      {/* Preview */}
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-[11px] font-mono leading-relaxed
                      max-h-56 overflow-auto whitespace-pre-wrap break-all">
{text}
      </pre>

      {/* Copy button */}
      <button
        type="button"
        onClick={handleCopy}
        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
          copied
            ? 'bg-emerald-500 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        {copied ? 'คัดลอกแล้ว!' : 'คัดลอกข้อความทั้งก้อน'}
      </button>
    </div>
  );
};

export default CredentialsCopyBlock;
